/**
 * UserDataForm - Modal Form Component for Collecting User Birth Details
 * 
 * This component provides a modal popup form that collects user birth details
 * before entering the chat interface. It replaces the step-by-step data collection
 * in the chat with a single comprehensive form.
 * 
 * Features:
 * - Modal overlay with backdrop
 * - Form validation for all fields
 * - Date and time input handling
 * - Place/city input with suggestions
 * - Submit and cancel functionality
 * - Responsive design for mobile and desktop
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onSubmit - Function to submit the form data
 * @returns {JSX.Element} The modal form interface
 */

import React, { useState, useEffect, useRef } from 'react';
import './UserDataForm.css';
import astroBotAPI from '../services/api';
import { filterCities } from '../data/indianCities';
import logger from '../utils/logger';
import { sanitizeInput, validatePhone } from '../utils/sanitize';

const UserDataForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dob: '',
    tob: '',
    place: '',
    timezone: 'Asia/Kolkata',
    mode: 'kundli' // 'kundli' or 'horary'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Autocomplete state for place field
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const placeInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        phone: '',
        dob: '',
        tob: '',
        place: '',
        timezone: 'Asia/Kolkata',
        mode: 'kundli'
      });
      setErrors({});
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [isOpen]);

  // Handle place input changes with autocomplete
  const handlePlaceChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      place: value
    }));
    
    // Clear error when user starts typing
    if (errors.place) {
      setErrors(prev => ({
        ...prev,
        place: ''
      }));
    }
    
    // Filter cities based on input
    if (value.trim().length >= 2) {
      const suggestions = filterCities(value, 10);
      setPlaceSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Handle place suggestion selection
  const handlePlaceSelect = (city) => {
    setFormData(prev => ({
      ...prev,
      place: city
    }));
    setPlaceSuggestions([]);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    // Focus back on input after selection
    if (placeInputRef.current) {
      placeInputRef.current.focus();
    }
  };

  // Handle keyboard navigation in suggestions
  const handlePlaceKeyDown = (e) => {
    if (!showSuggestions || placeSuggestions.length === 0) {
      if (e.key === 'ArrowDown' && formData.place.trim().length >= 2) {
        const suggestions = filterCities(formData.place, 10);
        if (suggestions.length > 0) {
          setPlaceSuggestions(suggestions);
          setShowSuggestions(true);
          setSelectedSuggestionIndex(0);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < placeSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < placeSuggestions.length) {
          handlePlaceSelect(placeSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        if (placeInputRef.current) {
          placeInputRef.current.blur();
        }
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        placeInputRef.current &&
        !placeInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation (always required)
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    // Phone number validation (optional but if provided, must be valid)
    if (formData.phone.trim()) {
      // Sanitize phone input
      const sanitizedPhone = sanitizeInput(formData.phone.trim());
      if (!validatePhone(sanitizedPhone)) {
        newErrors.phone = 'Please enter a valid phone number (e.g., 9876543210 or +91 9876543210)';
      }
    }
    
    // For Kundli mode, validate all birth details
    if (formData.mode === 'kundli') {
      // Date of birth validation
      if (!formData.dob) {
        newErrors.dob = 'Date of birth is required';
      } else {
        const dobDate = new Date(formData.dob);
        const today = new Date();
        if (dobDate > today) {
          newErrors.dob = 'Date of birth cannot be in the future';
        } else if (dobDate < new Date('1900-01-01')) {
          newErrors.dob = 'Date of birth cannot be before 1900';
        }
      }
      
      // Time of birth validation
      if (!formData.tob) {
        newErrors.tob = 'Time of birth is required';
      } else {
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(formData.tob)) {
          newErrors.tob = 'Please enter time in HH:MM format (24-hour)';
        }
      }
      
      // Place validation
      if (!formData.place.trim()) {
        newErrors.place = 'Birth place is required';
      } else if (formData.place.trim().length < 2) {
        newErrors.place = 'Place name must be at least 2 characters';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format the data for the backend based on mode
      // Sanitize all inputs before sending
      const formattedData = {
        name: sanitizeInput(formData.name.trim()),
        phone: formData.phone.trim() ? sanitizeInput(formData.phone.trim()) : '', // Include phone number
        mode: formData.mode,
        timezone: formData.timezone
      };
      
      // Add birth details only for Kundli mode
      if (formData.mode === 'kundli') {
        formattedData.dob = formData.dob;
        formattedData.tob = formData.tob + ':00'; // Add seconds
        formattedData.place = formData.place.trim();
        
        // Fire-and-forget: submit to backend to store in Google Sheet
        // Do not block UX if Sheets write fails
        astroBotAPI.sendFormData(formattedData).catch(() => {});
      }

      await onSubmit(formattedData);
      onClose();
    } catch (error) {
      logger.error('Error submitting form:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-data-form-overlay" onClick={handleBackdropClick}>
      <div className="user-data-form-modal">
        <div className="form-header">
          <h2>🔮 Astrology Consultation</h2>
          <p>Choose your consultation method and provide the required details</p>
          <button 
            className="close-btn" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-data-form">
          {/* Consultation Mode Selection */}
          <div className="form-group">
            <label>Consultation Method *</label>
            <div className="mode-selection">
              <label className="mode-option">
                <input
                  type="radio"
                  name="mode"
                  value="kundli"
                  checked={formData.mode === 'kundli'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <div className="mode-card">
                  <h3>🔮 Kundli Analysis</h3>
                  <p>Complete birth details required</p>
                  <small>Date, Time & Place of Birth</small>
                </div>
              </label>
              <label className="mode-option">
                <input
                  type="radio"
                  name="mode"
                  value="horary"
                  checked={formData.mode === 'horary'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <div className="mode-card">
                  <h3>🎯 KP Horary</h3>
                  <p>No birth details needed</p>
                  <small>Just your name & a number</small>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className={errors.name ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number (optional)"
              className={errors.phone ? 'error' : ''}
              disabled={isSubmitting}
              aria-label="Phone Number"
              aria-invalid={errors.phone ? 'true' : 'false'}
              maxLength={20}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
            {!errors.phone && (
              <span className="helper-text">Optional: For personalized updates and consultations</span>
            )}
          </div>

          {/* Birth Details - Only show for Kundli mode */}
          {formData.mode === 'kundli' && (
            <>
              <div className="form-group">
                <label htmlFor="dob">Date of Birth *</label>
                <input
                  type="date"
                  id="dob"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className={errors.dob ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.dob && <span className="error-message">{errors.dob}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="tob">Time of Birth *</label>
                <input
                  type="time"
                  id="tob"
                  name="tob"
                  value={formData.tob}
                  onChange={handleInputChange}
                  className={errors.tob ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.tob && <span className="error-message">{errors.tob}</span>}
              </div>

              <div className="form-group place-autocomplete-group">
                <label htmlFor="place">Birth Place/City *</label>
                <div className="place-input-wrapper">
                  <input
                    ref={placeInputRef}
                    type="text"
                    id="place"
                    name="place"
                    value={formData.place}
                    onChange={handlePlaceChange}
                    onKeyDown={handlePlaceKeyDown}
                    onFocus={() => {
                      if (formData.place.trim().length >= 2 && placeSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    placeholder="Type city name (e.g., Kanpur, Mumbai, Delhi)"
                    className={errors.place ? 'error' : ''}
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                  {showSuggestions && placeSuggestions.length > 0 && (
                    <div 
                      ref={suggestionsRef}
                      className="place-suggestions-dropdown"
                    >
                      {placeSuggestions.map((city, index) => (
                        <div
                          key={city}
                          className={`suggestion-item ${
                            index === selectedSuggestionIndex ? 'selected' : ''
                          }`}
                          onClick={() => handlePlaceSelect(city)}
                          onMouseEnter={() => setSelectedSuggestionIndex(index)}
                        >
                          {city}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.place && <span className="error-message">{errors.place}</span>}
                {formData.place.trim().length > 0 && formData.place.trim().length < 2 && (
                  <span className="helper-text">Type at least 2 characters to see suggestions</span>
                )}
              </div>
            </>
          )}

          {/* Horary Mode Info */}
          {formData.mode === 'horary' && (
            <div className="horary-info">
              <div className="info-card">
                <h3>🎯 KP Horary Analysis</h3>
                <p>Don't worry if you don't know your birth details! KP Horary astrology can provide accurate predictions using just a number.</p>
                <ul>
                  <li>✅ No birth date, time, or place needed</li>
                  <li>✅ Accurate predictions based on cosmic timing</li>
                  <li>✅ Perfect for urgent questions</li>
                  <li>✅ You'll be asked to choose a number (1-249) during chat</li>
                </ul>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="timezone">Timezone</label>
            <select
              id="timezone"
              name="timezone"
              value={formData.timezone}
              onChange={handleInputChange}
              disabled={isSubmitting}
            >
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
            </select>
          </div>

          {errors.submit && (
            <div className="submit-error">
              <span className="error-message">{errors.submit}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner"></div>
                  {formData.mode === 'kundli' ? 'Generating Kundli...' : 'Starting Horary...'}
                </>
              ) : (
                formData.mode === 'kundli' ? 'Generate Kundli & Start Chat' : 'Start Horary Analysis'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDataForm;
